/* eslint-disable max-lines */
import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useThemeContext } from '../../../utils/providers/theme-context';
import {
  isValidPhoneNumber,
  CountryCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';

const E164_MAX_DIGITS = 15;

/**
 * Phone validation for form schemas. More lenient than isValidE164:
 * - Strips common formatting (spaces, dashes, parens)
 * - Recovers numbers where the trunk prefix (leading 0) was included after
 *   the country code (e.g. +31 06 12345678 → +31 6 12345678). This happens
 *   when numbers were saved via old forms or imports before the phone component
 *   enforced E.164 normalisation.
 * - Falls back to strict isValidPhoneNumber for everything else.
 */
export const isPhoneValidForForm = (
  value: string | null | undefined
): boolean => {
  if (!value) return true;
  if (/^\+?\d{0,4}$/.test(value)) return true;

  const stripped = value.replace(/[\s\-().]/g, '');

  try {
    if (isValidPhoneNumber(stripped)) return true;
  } catch {
    // continue to trunk-prefix recovery
  }

  // Trunk-prefix recovery: +31 06 12345678 → +31 6 12345678
  try {
    const parsed = parsePhoneNumberFromString(stripped);
    if (parsed?.country) {
      const national = parsed.nationalNumber;
      if (national.startsWith('0')) {
        const fixed = parsePhoneNumberFromString(
          national.slice(1),
          parsed.country as CountryCode
        );
        if (fixed?.isValid()) return true;
      }
    }
  } catch {
    // ignore
  }

  // Last-resort: accept any E.164-shaped string (+ followed by 7–15 digits).
  // This prevents legacy numbers stored before strict validation was enforced
  // from blocking form submission when other fields are being updated.
  if (/^\+[1-9]\d{6,14}$/.test(stripped)) return true;

  return false;
};

export const normalizeToE164Like = (raw: string, country?: string): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  let v = trimmed.replace(/^\s*00/, '+');

  if (v.startsWith('+')) {
    // "+0..." is never a valid E.164 (no ITU-T country code starts with 0).
    // It's a local-format number that got a stray "+" prepended — strip it
    // and re-parse with country context to produce the real E.164.
    if (v.startsWith('+0') && country) {
      try {
        const parsed = parsePhoneNumberFromString(
          v.slice(1), // strip the invalid leading "+"
          country.toUpperCase() as CountryCode
        );
        if (parsed?.isValid()) return parsed.format('E.164');
      } catch {
        // noop
      }
    }

    // Looks like a proper international number — clean and return
    v = v.replace(/[^\d+]/g, '');
    v = v.replace(/\+/g, (m, i) => (i === 0 ? m : ''));
    if (v === '+') return '';
    const digits = v.replace(/\D/g, '').slice(0, E164_MAX_DIGITS);
    return digits ? `+${digits}` : '';
  }

  // Local format without any prefix — use country context to convert to E.164
  if (country) {
    try {
      const parsed = parsePhoneNumberFromString(
        trimmed,
        country.toUpperCase() as CountryCode
      );
      if (parsed?.isValid()) return parsed.format('E.164');
    } catch {
      // noop
    }
  }

  // Fallback: prepend + (may produce invalid E.164 for ambiguous local numbers)
  v = v.replace(/[^\d+]/g, '');
  if (v === '+') return '';
  const digits = v.replace(/\D/g, '').slice(0, E164_MAX_DIGITS);
  return digits ? `+${digits}` : '';
};

export const isValidE164 = (val: string, country?: string): boolean => {
  if (!val) return false;
  if (!/^\+[1-9]\d{1,14}$/.test(val)) return false;
  if (country) {
    try {
      return isValidPhoneNumber(val, country.toUpperCase() as CountryCode);
    } catch {
      return false;
    }
  }
  try {
    return isValidPhoneNumber(val);
  } catch {
    return false;
  }
};

export type PhoneFieldProps = {
  id?: string;
  label?: string | React.ReactNode;
  value?: string;
  onChange?: (e164: string) => void;
  defaultCountry?: string;
  required?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  wrapperClass?: string;
  labelClass?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  error?: string;
  messages?: { required?: string; invalid?: string };
  name?: string;
};

const PhoneField: React.FC<PhoneFieldProps> = ({
  id = 'phone',
  label = 'Phone',
  value,
  onChange,
  defaultCountry = 'NL',
  required,
  placeholder,
  size = 'medium',
  className,
  wrapperClass,
  labelClass,
  autoComplete = 'tel',
  autoFocus,
  disabled,
  error,
  name
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const positionDropdown = () => {
      const dropdown = wrapper.querySelector(
        '.react-international-phone-country-selector-dropdown'
      ) as HTMLElement | null;
      const button = wrapper.querySelector(
        '.react-international-phone-country-selector-button'
      ) as HTMLElement | null;
      if (!dropdown || !button) return;
      const buttonRect = button.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      dropdown.style.position = 'fixed';
      dropdown.style.top = `${buttonRect.bottom + 2}px`;
      dropdown.style.left = `${wrapperRect.left}px`;
      dropdown.style.zIndex = '9999';
      dropdown.style.width = `${wrapperRect.width}px`;
    };

    const observer = new MutationObserver(positionDropdown);
    observer.observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    return () => observer.disconnect();
  }, []);

  const themeContext = useThemeContext();
  const theme = themeContext?.theme ?? 'light';
  const getColour = themeContext?.getColour;

  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const safeColour = (token: string): string =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getColour?.(token as any) ?? 'inherit';

  const text = safeColour('normalText');
  const surface = safeColour('secondLayer');
  const border = safeColour('border');
  const hoverBg = safeColour('firstLayer');
  const disabledBg = pick('rgba(17,24,39,0.03)', 'rgba(255,255,255,0.06)');
  const disabledText = safeColour('inactiveText');

  // Match Tailwind h-9 / h-10 / h-12 exactly (in rem so they scale with root font-size)
  const height =
    size === 'small' ? '2.25rem' : size === 'large' ? '3rem' : '2.5rem';
  const fontSize = size === 'large' ? '1rem' : '0.875rem';
  const dropdownTop =
    size === 'small'
      ? 'calc(2.25rem + 2px)'
      : size === 'large'
        ? 'calc(3rem + 2px)'
        : 'calc(2.5rem + 2px)';

  const cssVars: React.CSSProperties = {
    '--react-international-phone-background-color': surface,
    '--react-international-phone-text-color': text,
    '--react-international-phone-border-color': border,
    '--react-international-phone-height': height,
    '--react-international-phone-font-size': fontSize,
    '--react-international-phone-border-radius': '1rem',
    '--react-international-phone-country-selector-background-color': surface,
    '--react-international-phone-country-selector-background-color-hover':
      hoverBg,
    '--react-international-phone-country-selector-border-color': border,
    '--react-international-phone-dropdown-item-background-color': surface,
    '--react-international-phone-dropdown-item-text-color': text,
    '--react-international-phone-selected-dropdown-item-background-color':
      hoverBg,
    '--react-international-phone-selected-dropdown-item-text-color': text,
    '--react-international-phone-disabled-background-color': disabledBg,
    '--react-international-phone-disabled-text-color': disabledText,
    '--react-international-phone-dropdown-top': dropdownTop,
    width: '100%'
  } as React.CSSProperties;

  return (
    <div
      ref={wrapperRef}
      className={wrapperClass ?? 'w-full'}
      style={{ position: 'relative', zIndex: 1 }}
    >
      {label && (
        <label
          htmlFor={id}
          className={cn('block text-sm font-semibold mb-2', labelClass)}
        >
          {label}
        </label>
      )}

      <div
        className={cn('block', error ? 'ring-1 ring-red-500 rounded-2xl' : '')}
        style={cssVars as React.CSSProperties}
      >
        <PhoneInput
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          defaultCountry={defaultCountry.toLowerCase() as any}
          value={value ?? ''}
          onChange={phone => {
            // Suppress bare dial-code emissions (e.g. "+31") that appear when
            // the field is empty — pass empty string so RHF doesn't store a
            // partial value that fails "valid phone number" validation.
            onChange?.(/^\+\d{1,4}$/.test(phone || '') ? '' : phone);
          }}
          disabled={disabled}
          preferredCountries={['nl', 'be', 'de', 'gb', 'fr', 'us']}
          className="w-full"
          inputClassName={cn('flex-1 w-full', className)}
          inputProps={{
            id,
            name,
            required,
            autoComplete,
            autoFocus,
            placeholder,
            'aria-invalid': !!error || undefined,
            'aria-describedby': error ? `${id}-error` : undefined
          }}
        />
      </div>

      {error && (
        <p
          id={`${id}-error`}
          className="text-sm mt-1"
          style={{ color: getColour?.('danger') ?? 'red' }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default PhoneField;
